/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import { formatter } from "@eisinc/App/Utilities/PaymentCenterUtilities";
import { InvoicePaymentModel } from "@eisinc/XDL/interfaces";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Checkbox from "@insite/mobius/Checkbox";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    dueDateRanges: GetSystemListValuesModel[];
    setDueDateRanges: (ranges: GetSystemListValuesModel[]) => void;
    invoicePaymentItems?: InvoicePaymentModel[];
}

type Props = OwnProps;

export interface InvoicesRangeStyles {
    wrapper?: InjectableCss;
    checkBoxesWrapper?: InjectableCss;
    checkboxItemWrapper?: InjectableCss;
    checkBoxLabelWrapper?: InjectableCss;
    checkBoxTotal?: InjectableCss;
}

export const invoicesRangeStyles: InvoicesRangeStyles = {}
   
const InvoicesRange: React.FC<Props> = ({
    setDueDateRanges,
    dueDateRanges,
    invoicePaymentItems,
}) => {
    const [isRangesUpdates, setIsRangesUpdates] = useState(false);
    const [filteredInvoicePayments, setFilteredInvoicePayments] = React.useState<InvoicePaymentModel[] | undefined>();
    const styles = invoicesRangeStyles;

    const rangeChangeHandler = (index: number, value: boolean) => {
        const newOptions = [...dueDateRanges];
        newOptions[index] = { ...newOptions[index], isChecked: value };
        setDueDateRanges(newOptions);
    }

    useEffect(() => {
        setFilteredInvoicePayments(invoicePaymentItems?.filter(invoice => invoice.invoiceAmount > 0 && !invoice.hideCheckBox));
    }, [invoicePaymentItems]);

    useEffect(() => {
        // EIS-4035 - Adding date range total
        if (dueDateRanges.length && filteredInvoicePayments?.length && !isRangesUpdates) {
            dueDateRanges.forEach((range) => {
                let rangeDueDatePast: any;
                if (typeof range.name === "string") {
                    try {
                        const sanitizedString = range.name.replace(/Infinity/g, '"Infinity"');
                        rangeDueDatePast = JSON.parse(sanitizedString);
                    } catch (error) {
                        rangeDueDatePast = [0, 0]; // Fallback to a default range
                    }
                } else {
                    rangeDueDatePast = range.name;
                }

                range.totalInvoicesAmount = 0;

                const invoicesInRange = filteredInvoicePayments.filter((invoice) => {
                    const dueDatePast = invoice.dueDatePast;
                    return (
                        dueDatePast >= rangeDueDatePast[0] &&
                        dueDatePast <= rangeDueDatePast[1]
                    );
                });

                range.totalInvoicesAmount = invoicesInRange.reduce((total, invoice) => {
                    return total + (invoice.invoiceAmount || 0);
                }, 0);
            });
            setIsRangesUpdates(true);
        }
    }, [dueDateRanges, filteredInvoicePayments, isRangesUpdates]);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography variant="h4">{translate("Pay All Invoices Within Range:")}</Typography>
            <StyledWrapper {...styles.checkBoxesWrapper}>
                {
                    dueDateRanges?.map((option, index) => {
                        if (option.description === 'Show All') {
                            return null;
                        }
                        return (
                            <StyledWrapper {...styles.checkboxItemWrapper} key={index}>
                                <Checkbox
                                    checked={option.isChecked}
                                    onChange={(e, value) => rangeChangeHandler(index, value)}
                                >
                                   <StyledWrapper {...styles.checkBoxLabelWrapper}>
                                        <Typography>{option.description}</Typography>
                                        <Typography {...styles.checkBoxTotal}>{formatter.format(option.totalInvoicesAmount || 0.00)}</Typography>
                                   </StyledWrapper>
                                </Checkbox>
                            </StyledWrapper>
                        );
                    })
                }
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default InvoicesRange;
